// Pixi 8.9.2 BindGroup 'change' listeners. Every shader's bind group subscribes
// to the 'change' event of each texture source and style it binds. Two problems:
//
// - Tearing a clip down costs (its materials) x (live materials). Every MSDF
//   letter material binds its atlas and Texture.WHITE, so those sources carry
//   one listener per live letter (~17k on WHITE at Dumbo 1x), and
//   eventemitter3's removeListener scans and copies that whole array for each
//   material destroyed: ~1 s per edit at Dumbo 1x, ~3.5 s at 4x. Here a
//   texture source or style keeps its bind groups in a Set (O(1) add and
//   remove) behind a single 'change' listener that notifies them.
// - setResource calls `off` on the new resource instead of the one it
//   replaces, so a swapped-out texture keeps the bind group's listener for
//   good. Here the replaced resource is unsubscribed, unless the bind group
//   still binds it at another slot.
//
// A bind group still sees every change of what it binds (resize, style update,
// unload, destroy) with the same argument; Pixi's own onResourceChange does the
// rest. Other resources (buffers) keep eventemitter3 listeners.
import { BindGroup, TextureSource, TextureStyle } from 'pixi.js';

const bindGroupsByResource = new WeakMap();

const keepsSet = (resource) => resource instanceof TextureSource || resource instanceof TextureStyle;

function notifyBindGroups(resource) {
	const bindGroups = bindGroupsByResource.get(resource);
	// A destroyed resource drops all its listeners right after this 'change'.
	if (resource.destroyed) bindGroupsByResource.delete(resource);
	for (const bindGroup of bindGroups) bindGroup.onResourceChange(resource);
}

function subscribe(resource, bindGroup) {
	if (!keepsSet(resource)) {
		resource.on?.('change', bindGroup.onResourceChange, bindGroup);
		return;
	}
	let bindGroups = bindGroupsByResource.get(resource);
	if (!bindGroups) {
		bindGroups = new Set();
		bindGroupsByResource.set(resource, bindGroups);
		resource.on('change', notifyBindGroups);
	}
	bindGroups.add(bindGroup);
}

// Reads `resource.off` like 8.9.2 does, so a slot nulled by its resource's destroy throws as it does there.
function unsubscribe(resource, bindGroup) {
	if (keepsSet(resource)) bindGroupsByResource.get(resource)?.delete(bindGroup);
	else resource.off?.('change', bindGroup.onResourceChange, bindGroup);
}

BindGroup.prototype.setResource = function setResource(resource, index) {
	const currentResource = this.resources[index];
	if (resource === currentResource) return;
	const boundElsewhere = Object.keys(this.resources).some((i) => i !== String(index) && this.resources[i] === currentResource);
	if (currentResource && !boundElsewhere) unsubscribe(currentResource, this);
	subscribe(resource, this);
	this.resources[index] = resource;
	this._dirty = true;
};

BindGroup.prototype.destroy = function destroy() {
	for (const i in this.resources) unsubscribe(this.resources[i], this);
	this.resources = null;
};
